import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { API_ENDPOINTS } from '../constants/api';
import { toProperCase } from '../utils/textUtils';
import './EditBusinessUnitModal.css';

interface BusinessUnit {
  business_id: string;
  business: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  phone?: string;
  is_current?: boolean;
}

interface EditBusinessUnitModalProps {
  businessUnit: BusinessUnit | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedBusinessUnit: BusinessUnit) => void;
  onDelete?: (businessUnitId: string) => void;
  userToken: string;
}

const EditBusinessUnitModal: React.FC<EditBusinessUnitModalProps> = ({
  businessUnit,
  isOpen,
  onClose,
  onSave,
  onDelete,
  userToken
}) => {
  const [formData, setFormData] = useState<BusinessUnit>({
    business_id: '',
    business: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'USA',
    phone: '',
    is_current: true
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState('');

  // Update form data when business unit changes
  useEffect(() => {
    if (businessUnit) {
      setFormData({
        business_id: businessUnit.business_id,
        business: businessUnit.business,
        address_line1: businessUnit.address_line1 || '',
        address_line2: businessUnit.address_line2 || '',
        city: businessUnit.city || '',
        state: businessUnit.state || '',
        postal_code: businessUnit.postal_code || '',
        country: businessUnit.country || 'USA',
        phone: businessUnit.phone || '',
        is_current: businessUnit.is_current !== undefined ? businessUnit.is_current : true
      });
      // Reset delete confirmation when editing an existing business unit
      setShowDeleteConfirm(false);
    } else {
      // Reset form and delete confirmation when adding a new business unit
      setFormData({
        business_id: '',
        business: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        postal_code: '',
        country: 'USA',
        phone: '',
        is_current: true
      });
      setShowDeleteConfirm(false);
    }
  }, [businessUnit]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    // Handle checkbox for is_current
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
      return;
    }
    
    // Apply proper case conversion to specific fields
    const fieldsToProperCase = ['business', 'address_line1', 'address_line2', 'city'];
    const processedValue = fieldsToProperCase.includes(name) ? toProperCase(value) : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');

    try {
      const isEditMode = !!businessUnit;
      const { business_id, ...requestData } = formData;
      
      const url = isEditMode 
        ? `${API_ENDPOINTS.BUSINESS_UNITS}/${businessUnit.business_id}`
        : API_ENDPOINTS.BUSINESS_UNITS;
      
      const response = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`,
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to ${isEditMode ? 'update' : 'create'} business unit`);
      }

      const savedBusinessUnit = await response.json();
      onSave(savedBusinessUnit);
      onClose();
    } catch (err) {
      console.error(`Error ${businessUnit ? 'updating' : 'creating'} business unit:`, err);
      setError(err instanceof Error ? err.message : 'Failed to update business unit');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setError('');
    setShowDeleteConfirm(false);
    onClose();
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!businessUnit || !onDelete) return;
    
    setIsDeleting(true);
    setError('');

    try {
      const response = await fetch(`${API_ENDPOINTS.BUSINESS_UNITS}/${businessUnit.business_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${userToken}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete business unit');
      }

      onDelete(businessUnit.business_id);
      onClose();
    } catch (err) {
      console.error('Error deleting business unit:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete business unit');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    // Reopen the edit modal since user cancelled the delete
    if (businessUnit) {
      // We need to call the parent's onClose and then reopen
      // For now, just hide the delete confirmation and let the parent handle reopening
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>{businessUnit ? 'Edit Business Unit' : 'Add New Business Unit'}</h2>
          <button className="modal-close" onClick={handleCancel}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form" autoComplete="off">
          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="business">Business Name *</label>
            <input
              type="text"
              id="business"
              name="business"
              value={formData.business}
              onChange={handleInputChange}
              required
              placeholder="Enter business name"
              autoComplete="off"
            />
          </div>

          <div className="form-group">
            <label htmlFor="address_line1">Address Line 1</label>
            <input
              type="text"
              id="address_line1"
              name="address_line1"
              value={formData.address_line1}
              onChange={handleInputChange}
              placeholder="Street address"
            />
          </div>

          <div className="form-group">
            <label htmlFor="address_line2">Address Line 2</label>
            <input
              type="text"
              id="address_line2"
              name="address_line2"
              value={formData.address_line2}
              onChange={handleInputChange}
              placeholder="Suite, unit, etc."
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="city">City</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                placeholder="City"
              />
            </div>
            <div className="form-group">
              <label htmlFor="state">State</label>
              <input
                type="text"
                id="state"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                placeholder="State"
              />
            </div>
            <div className="form-group">
              <label htmlFor="postal_code">Postal Code</label>
              <input
                type="text"
                id="postal_code"
                name="postal_code"
                value={formData.postal_code}
                onChange={handleInputChange}
                placeholder="ZIP code"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="country">Country</label>
              <input
                type="text"
                id="country"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                placeholder="Country"
              />
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="Phone number"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_current"
                checked={formData.is_current}
                onChange={handleInputChange}
              />
              <span className="checkbox-text">Currently Active</span>
            </label>
          </div>

          <div className="modal-actions">
            {businessUnit && (
              <button 
                type="button" 
                onClick={handleDeleteClick}
                className="btn-delete"
                disabled={isSaving || isDeleting}
              >
                <FontAwesomeIcon icon={faTrash} />
                Delete
              </button>
            )}
            <button 
              type="button" 
              onClick={handleCancel}
              className="btn-secondary"
              disabled={isSaving || isDeleting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={isSaving || isDeleting}
            >
              {isSaving ? 'Saving...' : (businessUnit ? 'Save Changes' : 'Create Business Unit')}
            </button>
          </div>
        </form>
      </div>

      {/* Delete Confirmation Modal - Only show when editing an existing business unit */}
      {showDeleteConfirm && businessUnit && (
        <div className="modal-overlay">
          <div className="modal-content delete-confirm-modal">
            <div className="modal-header">
              <h2>Confirm Delete</h2>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this business unit?</p>
              <p className="warning-text">
                <strong>Warning:</strong> This will also delete all associated farms and fields.
              </p>
            </div>
            <div className="modal-actions">
              <button 
                type="button" 
                onClick={handleDeleteCancel}
                className="btn-secondary"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                type="button" 
                onClick={handleDeleteConfirm}
                className="btn-delete"
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditBusinessUnitModal; 